import React, { useState, useEffect } from 'react';
import { Coupon } from '../../../../core/Interface/repositoryInterfaces';
import { validateCoupon, ValidationError } from '../../../../core/form/validationForm';
import { InputField, CalendarField, DropdownField, SwitchField } from '../input/inputField';
import { TextInput, DropdownInput, DatePicker } from '../../../components/inputs';
import { handleChange, formatDatetoString } from '../../../../core/utils/utils';
import { getErrorMessage } from '../../../../core/utils/errorUtils';
import { TYPE_DISCOUNT, TYPE_GENERAL_PRODUCT, LIMIT_PER_USER } from '../../../../core/data/options/options';
import { convertStringToLocalDate, convertDateToLocalDateString } from '../../../../core/utils/dateUtils';
import BaseModal from './baseModal';
import { UpdateCouponModalProps } from '../../../../core/Interface/modalInterface';
import usePost from '../../../../core/hooks/api/usePost';
import usePut from '../../../../core/hooks/api/usePut';
import API_ENDPOINTS from '../../../../core/constant/API_ENDPOINTS'; // Asegúrate de que este path sea correcto

const CouponesModal: React.FC<UpdateCouponModalProps> = ({ selectedCoupon, onUpdateSuccess, packageTypes, packages }) => {
    const [id, setId] = useState<number>(selectedCoupon ? selectedCoupon.id : -1);
    const [couponData, setCouponData] = useState<any>({} as Coupon);
    const [errors, setErrors] = useState<ValidationError[]>([]);
    const [show, setShow] = useState<boolean>(false);
    const { post: postCoupon, loading: postLoading } = usePost(API_ENDPOINTS.coupons);
    const { put: putCoupon, loading: putLoading } = usePut(API_ENDPOINTS.coupons);

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        handleChange(e, setCouponData);
    };

    const handleClose = () => {
        setShow(false);
        setErrors([]);
        setCouponData([]);
        setId(-1);
    };

    useEffect(() => {
        if (selectedCoupon) {
            setCouponData({
                name: selectedCoupon.name,
                code: selectedCoupon.code,
                limitPerUser: selectedCoupon.limitPerUser || true,
                typeDiscount: selectedCoupon.typeDiscount,
                typeGeneralProduct: selectedCoupon.typeGeneralProduct,
                amount: selectedCoupon.amount,
                packetId: selectedCoupon.packetId,
                packageTypeId: selectedCoupon.packageTypeId,
                dateExpired: selectedCoupon.dateExpired && !isNaN(new Date(selectedCoupon.dateExpired).getTime())
                    ? convertStringToLocalDate(selectedCoupon.dateExpired)
                    : convertStringToLocalDate(formatDatetoString(new Date())),
                status: selectedCoupon.status !== undefined && selectedCoupon.status !== null ? selectedCoupon.status : true,
            })
            setId(selectedCoupon.id);
            setShow(true);
        }
    }, [selectedCoupon]);

    const handleConfirmCreate = async (event: React.FormEvent) => {
        event.preventDefault();
        setErrors([]);

        try {
            const validated = validateCoupon(couponData);
            if (validated.length > 0) {
                setErrors(validated);
                return;
            }

            const payload = {
                ...couponData,
                packageTypeId: couponData.packageTypeId || null,
                packetId: couponData.packetId || null,
                dateExpired: convertDateToLocalDateString(couponData.dateExpired),
                amount: Number(couponData.amount),
            };

            if (id) {
                await putCoupon(id, { id, ...payload });
            } else {
                await postCoupon(payload);
            }

            if (onUpdateSuccess) onUpdateSuccess();
            handleClose();
        } catch (err) {
            setErrors([{ field: "server", message: "Ocurrió un error al intentar guardar el cupón. Inténtalo de nuevo." }]);
        }
    };


    return (
        <>
            <BaseModal
                show={show}
                title="Cupón"
                onClose={handleClose}
                onConfirm={handleConfirmCreate}
                loading={postLoading || putLoading}
            >
                <form onSubmit={handleConfirmCreate}>
                    <div className="row">
                        <div className="col-md-6">
                            <TextInput
                                label="Nombre"
                                name="name"
                                value={couponData.name || ''}
                                onChange={handleInputChange}
                                error={getErrorMessage(errors, "name")}
                            />
                        </div>
                        <div className="col-md-6">
                            <TextInput
                                label="Codigo de Cupón"
                                name="code"
                                value={couponData.code || ''}
                                onChange={handleInputChange}
                                error={getErrorMessage(errors, "code")}
                            />
                        </div>
                    </div>
                    <DropdownInput
                        label="Aplicación del Descuento"
                        name="typeGeneralProduct"
                        options={TYPE_GENERAL_PRODUCT}
                        value={couponData.typeGeneralProduct}
                        onChange={handleInputChange}
                        error={getErrorMessage(errors, "typeGeneralProduct")}
                    />

                    {couponData.typeGeneralProduct == 1 && (
                        <DropdownInput
                            label="Paquetes"
                            name="packetId"
                            value={couponData.packetId}
                            options={packages || []}
                            placeholder="Seleccione un paquete"
                            onChange={handleInputChange}
                            error={getErrorMessage(errors, "packetId")}
                        />
                    )}
                    {couponData.typeGeneralProduct == 2 && (
                        <DropdownInput
                            label="Categorías"
                            name="packageTypeId"
                            value={couponData.packageTypeId}
                            options={packageTypes || []}
                            placeholder="Seleccione una categoría"
                            onChange={handleInputChange}
                            error={getErrorMessage(errors, "packageTypeId")}
                        />
                    )}

                    <div className="row">
                        <div className="col-md-6">
                            <DropdownInput
                                label="Tipo de Descuento"
                                name="typeDiscount"
                                value={couponData.typeDiscount}
                                options={TYPE_DISCOUNT}
                                onChange={handleInputChange}
                                error={getErrorMessage(errors, "typeDiscount")}
                            />
                        </div>
                        <div className="col-md-6">
                            <InputField
                                label="Cantidad de Descuento"
                                name="amount"
                                value={couponData.amount || ''}
                                onChange={handleInputChange}
                                error={getErrorMessage(errors, "amount")}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <DropdownInput
                                label="Uso Limitado"
                                name="limitPerUser"
                                value={couponData.limitPerUser}
                                options={LIMIT_PER_USER}
                                onChange={handleInputChange}
                                error={getErrorMessage(errors, "limitPerUser")}
                            />
                        </div>
                        <div className="col-md-6">
                            <DatePicker
                                label="Fecha de Expiración"
                                name="dateExpired"
                                value={couponData.dateExpired}
                                onChange={(e) => setCouponData({ ...couponData, dateExpired: convertStringToLocalDate(e) })}
                                error={getErrorMessage(errors, "dateExpired")}
                            />
                        </div>
                    </div>
                    <SwitchField
                        label="Estado"
                        checked={couponData.status || ''}
                        onChange={handleInputChange}
                        name="status"
                    />
                    {errors.find(error => error.field === 'server') && (
                        <div className="alert alert-danger" role="alert">
                            {getErrorMessage(errors, "server")}
                        </div>
                    )}
                </form>
            </BaseModal>
        </>
    );
};

export default CouponesModal;